import React from 'react';

import * as ResourceApi from 'api/resources';
import IndexedSubSectionWrapper from "hoc/IndexedSubSectionWrapper";
import {createButtonSectionFactory} from "hoc/CreateButtonSection";
import {FBCommentQuestionToolTip} from 'components/break/tooltips/breaks-steps-section-tool-tips';
import {FormattedMessage, IntlProvider} from 'react-intl';
import {enUSmessage} from 'i18n/en-US.js';

import {
    ChecklistHeadlineToolTip,
    ChecklistIntroCopyToolTip,
    ChecklistHeaderImageToolTip,
    ChecklistListItemTitleToolTip,
    ChecklistListItemDescriptionToolTip,
    ChecklistChooseImageWidthToolTip,
    ChecklistListItemImageToolTip,
    ChecklistResultTitleToolTip,
    ChecklistResultDescriptionToolTip,
    ChecklistResultImageToolTip,
    ChecklistMinItemsToolTip,
    ChecklistMaxItemsToolTip
} from 'components/break/tooltips/checklist-tool-tips';

export default {
    name: "checklist",
    subSection: true,
    resource: true,
    hideIfResourceIsNoU: true,

    fields: [
        {
            type: 'contenteditable',
            name: 'headline',
            required: true,
            // Headline is only allowed if there is more than one step
            showField: (proxy) => (proxy.currentBaseResource.steps && proxy.currentBaseResource.steps.length > 1),

            props: {
                labelKey: 'editbreak.steps.checklist.headline',
                helpContent: <ChecklistHeadlineToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.headline'}/></IntlProvider>
            }
        },
        {
            type: 'contenteditable',
            name: 'introCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.checklist.intro_copy',
              maxLength: 500,
              placeholderKey: 'editbreak.steps.checklist.intro_copy.placeholder',
              validationRules: [
                  {
                      type: 'MarkupTextLimit',
                      params: {
                          limit: 500
                      }
                  }
              ],
              helpContent: <ChecklistIntroCopyToolTip/>,
              helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.intro_copy'}/></IntlProvider>
            }
        },
        {
            type: 'contenteditable',
            name: 'bodyCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.checklist.body_copy',
              placeholderKey: 'editbreak.steps.checklist.body_copy.placeholder'
            }
        },
        {
            type: 'contenteditable',
            name: 'commentQuestion',
            showField: (proxy) => (proxy.currentBaseResource.showFBComments && proxy.currentBaseResource.sponsoredType !== 'sponsored'),
            props: {
                labelKey: 'editbreak.steps.related_breaks.commentQuestion',
                helpContent: <FBCommentQuestionToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.breaks_steps.fbCommentQuestion'}/></IntlProvider>
            }
        },
        {
            type: 'image',
            name: 'headerImageId',
            required: false,
            idFor: 'headerImage',

            props: {
                labelKey: 'editbreak.steps.checklist.header_image',
                imageType: 'stepHeaderImage',
                helpContent: <ChecklistHeaderImageToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.header_image'}/></IntlProvider>
            }
        },
        {
            subSection: true,
            collection: true,
            name: 'itemGroups',
            sectionWrapper: IndexedSubSectionWrapper,
            resource: true,
            deletable: true,
            resourceType: ResourceApi.Types.ChecklistItemGroup,
            sortable: true,
            sortableName: "checklistItemGroupsOrder",
            toggleSortLabelKey: 'editbreak.steps.checklist.item_groups.reorder_toggle',

            props: {
                headerKey: 'editbreak.steps.checklist.item_groups.header',
                getCustomSectionHeader: (group, sectionIndex) => {
                  return  group.items && group.items[0] && group.items[0].title
                          && group.items[0].title.trim() !== "" ? "List Item " + (sectionIndex+1) + ": " + group.items[0].title : null;
                }

            },
            footerContent: createButtonSectionFactory(ResourceApi.Types.ChecklistItemGroup),
            fields: [
              {
                name: 'items.0',
                  subSection: true,
                  resource: true,

                props: {
                    headerKey: 'editbreak.steps.checklist.item.header'
                },

                fields: [
                  {
                    type: 'contenteditable',
                    name: 'title',
                    required: true,
                    props: {
                      labelKey: 'editbreak.steps.checklist.item.title',
                      helpContent: <ChecklistListItemTitleToolTip/>,
                      helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.item.title'}/></IntlProvider>
                    }
                  },
                  {
                    type: 'contenteditable',
                    name: 'description',
                    required: false,
                    props: {
                      labelKey: 'editbreak.steps.checklist.item.description',
                      helpContent: <ChecklistListItemDescriptionToolTip/>,
                      helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.item.description'}/></IntlProvider>
                    }
                  },
                  {
                    type: 'dropdown',
                    name: 'imageType',
                    required: true,
                    props: {
                      labelKey: 'editbreak.steps.checklist.item.type',
                      options: [
                        {
                          value: null,
                          textKey: 'editbreak.steps.checklist.item.type.none'
                        },
                        {
                          value: 'HALF',
                          textKey: 'editbreak.steps.checklist.item.type.half'
                        },
                        {
                          value: 'FULL',
                          textKey: 'editbreak.steps.checklist.item.type.full'
                        }
                      ],
                      helpContent: <ChecklistChooseImageWidthToolTip/>,
                      helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.item.type'}/></IntlProvider>
                    }
                  },
                  {
                    type: 'image',
                    name: 'imageId',
                    idFor: 'image',
                    required: false,
                    showField: (proxy) => (proxy.currentResource.imageType && proxy.currentResource.imageType === 'HALF'),
                    props: {
                      labelKey: 'editbreak.steps.checklist.item.image',
                      imageType: 'checkListHalf',
                      helpContent: <ChecklistListItemImageToolTip/>,
                      helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.item.image'}/></IntlProvider>
                    }
                  },
                  {
                    type: 'image',
                    name: 'imageId',
                    idFor: 'image',
                    required: false,
                    showField: (proxy) => (proxy.currentResource.imageType && proxy.currentResource.imageType === 'FULL'),
                    props: {
                      labelKey: 'editbreak.steps.checklist.item.image',
                      imageType: 'checkListFull',
                      helpContent: <ChecklistListItemImageToolTip/>,
                      helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.item.image'}/></IntlProvider>
                    }
                  }
                ]
              }
            ]
        }, // end item groups
        {
          name: 'results',
          subSection: true,
          resource: true,
          collection: true,
          sectionWrapper: IndexedSubSectionWrapper,
          deletable: true,
          canDelete: (formProxy) => {
            var checklist = formProxy.parentCurrentResource;
            return checklist.results && checklist.results.length > 1;
          },

          sortable: true,
            sortableName: "checklistResultsOrder",
          toggleSortLabelKey: 'editbreak.steps.checklist.result.reorder_toggle',
          resourceType: ResourceApi.Types.ChecklistResult,

          props: {
              headerKey: 'editbreak.steps.checklist.result.header',
              getCustomSectionHeader: (result, sectionIndex) => {
                return  result.headline
                        && result.headline.trim() !== "" ? "Result " + (sectionIndex+1) + ": " + result.headline : null;
              }
          },

          footerContent: createButtonSectionFactory(ResourceApi.Types.ChecklistResult),

          fields: [
            {
              type: 'contenteditable',
              name: 'headline',
              required: true,
              props: {
                labelKey: 'editbreak.steps.checklist.result.title',
                helpContent: <ChecklistResultTitleToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.result.title'}/></IntlProvider>
              }
            },
            {
              type: 'contenteditable',
              name: 'text',
              required: false,
              props: {
                labelKey: 'editbreak.steps.checklist.result.description',
                helpContent: <ChecklistResultDescriptionToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.result.description'}/></IntlProvider>
              }
            },
            {
              type: 'image',
              name: 'imageId',
              idFor: 'image',
              required: false,
              props: {
                labelKey: 'editbreak.steps.checklist.result.image',
                imageType: 'checkListHalf',
                helpContent: <ChecklistResultImageToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.result.image'}/></IntlProvider>
              }
            },
            {
              type: 'number',
              name: 'minChecked',
              required: true,
              props: {
                labelKey: 'editbreak.steps.checklist.result.min_checked',
                min: 0,
                helpContent: <ChecklistMinItemsToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.result.min_checked'}/></IntlProvider>
              }
            },
            {
              type: 'number',
              name: 'maxChecked',
              required: true,
              props: {
                labelKey: 'editbreak.steps.checklist.result.max_checked',
                helpContent: <ChecklistMaxItemsToolTip/>,
                helpHeader:  <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.checklist.result.max_checked'}/></IntlProvider>
              }
            }
          ]
        }
    ]
}



// WEBPACK FOOTER //
// ./public/components/EditContent/configs/breaks/steps/checklist.js